// libs
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { IconChevronDownOutlineDuo18 } from 'nucleo-ui-outline-duo-18';

import { cn } from '@lyearn/style';
// components
import { Popover, Tooltip } from '@lyearn/ui';
import { Link } from '@/components/Link';
import { useSidebarCollapse } from '@/components/SidebarCollapse';
import useNavigation from '@/hooks/useNavigation';

// hooks
import { SideBarMode } from '../../hooks/useSidebar';
import { useSidebarFlyout } from './FlyoutPanel/context';
import { useSidebarDisclosure } from './DisclosureContext';
import FlyoutPanel from './FlyoutPanel';
import { sidebarIconStrokeWidth } from './nav-icon';
import { FLYOUT_PANEL_WIDTH } from './style';
import {
  SIDEBAR_DUO_FILL,
  SIDEBAR_FLYOUT_SURFACE,
  SIDEBAR_FOCUS_RING,
  SIDEBAR_ICON_HOVER,
  SIDEBAR_LABEL_HIDDEN,
  SIDEBAR_LABEL_TRANSITION,
  SIDEBAR_LINE_FILL,
  SIDEBAR_ROW_ACTIVE,
  SIDEBAR_ROW_DISABLED,
  SIDEBAR_ROW_REST,
  SIDEBAR_TEXT,
  SIDEBAR_TEXT_ACTIVE,
  SIDEBAR_TEXT_COLOR,
} from './theme';
import { SidebarNavItem, SidebarNavSubItem } from './types';
import { isSidebarItemActive } from './utils';

const FLYOUT_PANEL_CLASSES = { Popover: SIDEBAR_FLYOUT_SURFACE };

const OPEN_FLYOUT_KEYS = ['ArrowRight', 'ArrowDown'];
const FLYOUT_HOVER_CLOSE_DELAY_MS = 150;

interface RowState {
  isActive: boolean;
  isDisabled?: boolean;
  collapsed?: boolean;
}

const rowClasses = ({ isActive, isDisabled, collapsed }: RowState) =>
  cn(
    'group/row flex h-32 items-center overflow-hidden rounded-8 transition-[width] duration-300 ease-in-out',
    SIDEBAR_TEXT,
    collapsed ? 'w-32 pe-0' : 'w-full pe-12',
    isDisabled ? SIDEBAR_ROW_DISABLED : isActive ? SIDEBAR_ROW_ACTIVE : SIDEBAR_ROW_REST,
  );

const IconTile = ({ icon, isActive }: { icon?: React.ReactNode; isActive: boolean }) => (
  <span
    className={cn(
      'flex h-32 w-32 flex-shrink-0 items-center justify-center',
      SIDEBAR_ICON_HOVER,
      isActive ? SIDEBAR_TEXT_ACTIVE : cn(SIDEBAR_TEXT_COLOR, SIDEBAR_DUO_FILL),
    )}>
    {icon}
  </span>
);

const RowLabel = ({ label, collapsed }: { label: string; collapsed: boolean }) => (
  <span
    className={cn(
      'min-w-0 flex-1 truncate text-left',
      SIDEBAR_LABEL_TRANSITION,
      collapsed && SIDEBAR_LABEL_HIDDEN,
    )}>
    {label}
  </span>
);

const GroupChevron = ({ isOpen }: { isOpen: boolean }) => (
  <span
    className={cn(
      'flex h-32 w-32 flex-shrink-0 items-center justify-center transition-opacity duration-100',
      isOpen
        ? 'opacity-100'
        : 'opacity-0 group-hover/row:opacity-100 group-focus-visible/row:opacity-100',
    )}>
    <IconChevronDownOutlineDuo18
      className={cn(
        'h-14 w-14 opacity-50 transition-[transform,opacity] duration-150 group-hover/row:opacity-70',
        SIDEBAR_TEXT_ACTIVE,
        !isOpen && '-rotate-90',
      )}
      strokeWidth={sidebarIconStrokeWidth(14)}
    />
  </span>
);

const LeafRow = ({
  item,
  isActive,
  collapsed,
}: {
  item: SidebarNavItem;
  isActive: boolean;
  collapsed: boolean;
}) => {
  const body = (
    <>
      <IconTile icon={item.icon} isActive={isActive} />
      <RowLabel collapsed={collapsed} label={item.text} />
    </>
  );

  if (item.isDisabled || !item.to) {
    return (
      <span
        aria-disabled="true"
        className={rowClasses({ isActive: false, isDisabled: true, collapsed })}>
        {body}
      </span>
    );
  }

  return (
    <Tooltip placement="right" title={collapsed ? item.text : ''}>
      <Link
        aria-current={isActive ? 'page' : undefined}
        aria-label={collapsed ? item.text : undefined}
        className={cn(rowClasses({ isActive, collapsed }), SIDEBAR_FOCUS_RING)}
        to={item.to}>
        {body}
      </Link>
    </Tooltip>
  );
};

const ChildRow = ({ item, isActive }: { item: SidebarNavSubItem; isActive: boolean }) => {
  const isDisabled = item.isDisabled || !item.to;
  const classes = cn(
    'flex h-32 w-full items-center gap-6 overflow-hidden rounded-8 pe-12',
    SIDEBAR_TEXT,
    isDisabled ? SIDEBAR_ROW_DISABLED : isActive ? SIDEBAR_ROW_ACTIVE : SIDEBAR_ROW_REST,
  );

  const body = (
    <>
      <span aria-hidden="true" className="w-32 flex-shrink-0" />
      <span className="truncate">{item.text}</span>
    </>
  );

  if (isDisabled) {
    return (
      <span aria-disabled="true" className={classes}>
        {body}
      </span>
    );
  }

  return (
    <Link
      aria-current={isActive ? 'page' : undefined}
      className={cn(classes, SIDEBAR_FOCUS_RING)}
      to={item.to}>
      {body}
    </Link>
  );
};

const SidebarItem = ({
  sidebarItem,
  collapsed = false,
}: {
  sidebarItem: SidebarNavItem;
  mode?: SideBarMode;
  collapsed?: boolean;
}) => {
  const { id, icon, text, isDisabled } = sidebarItem;
  const children: SidebarNavSubItem[] = sidebarItem.children ?? [];

  const navigation = useNavigation();
  const listId = useId();
  const { openId, setOpen } = useSidebarDisclosure();
  const { closeFlyout, openFlyout, openFlyoutId } = useSidebarFlyout();
  const { isCollapsed: sidebarCollapsed, toggle: toggleSidebar } = useSidebarCollapse();

  const isGroup = children.length > 0 && !isDisabled;
  const hasActiveChild = children.some((child) => isSidebarItemActive(navigation.asPath, child));
  const isActive = isSidebarItemActive(navigation.asPath, sidebarItem);

  const isOpen = openId === id;
  const expanded = isGroup && isOpen && !collapsed;
  const highlighted = hasActiveChild && !expanded;

  const isFlyoutOpen = openFlyoutId === id;
  const [shouldAutoFocusFlyout, setShouldAutoFocusFlyout] = useState(false);

  useEffect(() => {
    if (!isFlyoutOpen) {
      setShouldAutoFocusFlyout(false);
    }
  }, [isFlyoutOpen]);

  const onFlyoutClose = useCallback(() => closeFlyout(id), [closeFlyout, id]);

  const onTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (OPEN_FLYOUT_KEYS.includes(event.key)) {
        event.preventDefault();
        setShouldAutoFocusFlyout(true);
        openFlyout(id);
      }
    },
    [id, openFlyout],
  );

  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverCloseTimeout = useCallback(() => {
    if (hoverCloseTimeoutRef.current) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearHoverCloseTimeout, [clearHoverCloseTimeout]);

  const onFlyoutMouseEnter = useCallback(() => {
    clearHoverCloseTimeout();
    openFlyout(id);
  }, [clearHoverCloseTimeout, id, openFlyout]);

  const onFlyoutMouseLeave = useCallback(() => {
    clearHoverCloseTimeout();
    hoverCloseTimeoutRef.current = setTimeout(() => closeFlyout(id), FLYOUT_HOVER_CLOSE_DELAY_MS);
  }, [clearHoverCloseTimeout, closeFlyout, id]);

  const onRailPress = useCallback(() => {
    closeFlyout(id);
    setOpen(id, true);

    if (sidebarCollapsed) {
      toggleSidebar();
    }
  }, [closeFlyout, id, setOpen, sidebarCollapsed, toggleSidebar]);

  if (!isGroup) {
    return <LeafRow collapsed={collapsed} isActive={isActive} item={sidebarItem} />;
  }

  if (collapsed) {
    return (
      <div className="contents" onMouseEnter={onFlyoutMouseEnter} onMouseLeave={onFlyoutMouseLeave}>
        <Popover
          open={isFlyoutOpen}
          onOpenChange={(open: boolean) => (open ? openFlyout(id) : closeFlyout(id))}>
          {({ anchorEl, setAnchorEl }) => (
            <>
              <button
                ref={setAnchorEl}
                aria-expanded={isFlyoutOpen}
                aria-haspopup="menu"
                aria-label={text}
                className={cn(
                  rowClasses({ isActive: highlighted, collapsed: true }),
                  'cursor-pointer',
                  SIDEBAR_FOCUS_RING,
                )}
                type="button"
                onClick={onRailPress}
                onKeyDown={onTriggerKeyDown}>
                <IconTile icon={icon} isActive={highlighted} />
                <RowLabel collapsed label={text} />
              </button>
              <Popover.Content
                className="z-50"
                classes={FLYOUT_PANEL_CLASSES}
                maxWidth={FLYOUT_PANEL_WIDTH}
                minWidth={FLYOUT_PANEL_WIDTH}
                position="right-start"
                renderAs="popper">
                <FlyoutPanel
                  currentPath={navigation.asPath}
                  items={children}
                  label={text}
                  shouldAutoFocus={shouldAutoFocusFlyout}
                  triggerElement={anchorEl}
                  onClose={onFlyoutClose}
                />
              </Popover.Content>
            </>
          )}
        </Popover>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col">
      <button
        aria-controls={listId}
        aria-expanded={isOpen}
        className={cn(
          rowClasses({ isActive: highlighted }),
          'cursor-pointer pe-0',
          SIDEBAR_FOCUS_RING,
        )}
        type="button"
        onClick={() => setOpen(id, !isOpen)}>
        <IconTile icon={icon} isActive={highlighted} />
        <RowLabel collapsed={false} label={text} />
        <GroupChevron isOpen={isOpen} />
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}>
        <ul
          aria-hidden={!expanded}
          className={cn(
            'relative flex min-h-0 flex-col gap-4 overflow-hidden transition-[padding] duration-300 ease-in-out',
            expanded ? 'pt-4' : 'pt-0',
          )}
          id={listId}
          {...({ inert: expanded ? undefined : '' } as Record<string, unknown>)}>
          <span
            aria-hidden="true"
            className={cn('absolute bottom-0 left-16 top-4 w-1', SIDEBAR_LINE_FILL)}
          />
          {children.map((child) => (
            <li key={child.id} className={cn(SIDEBAR_LABEL_TRANSITION, !expanded && 'opacity-0')}>
              <ChildRow isActive={isSidebarItemActive(navigation.asPath, child)} item={child} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default React.memo(SidebarItem);
